import { useRef, useState } from 'react';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle
} from 'design-system';

import { useFeedArticles } from '@/entities/feed-article';
import type { MonthlyRecapsArticle } from '@/features/monthly-recaps';

import { FeedSkeleton } from '../Feed/components/FeedSkeleton';
import { MonthlyRecapArticleDialog } from '../MarketArticlesModal/MonthlyRecapArticleDialog';
import { mapFeedInfoToArticle } from './lib/mapFeedInfoToArticle';
import { MonthlyRecapsListView } from './MonthlyRecapsListView';
import { MonthlyRecapsSingleView } from './MonthlyRecapsSingleView';

import type { MonthlyRecapsWidgetFilters } from './types';

export type { MonthlyRecapsWidgetFilters } from './types';

const MonthlyRecapsEmptyState = () => (
  <Empty className='min-h-40'>
    <EmptyHeader>
      <EmptyTitle>No monthly recap articles available.</EmptyTitle>
      <EmptyDescription>New monthly market analysis will appear here.</EmptyDescription>
    </EmptyHeader>
  </Empty>
);

const MonthlyRecapsErrorState = () => (
  <Alert className='m-4'>
    <AlertTitle>Monthly recaps unavailable</AlertTitle>
    <AlertDescription>Failed to load monthly recap articles.</AlertDescription>
  </Alert>
);

const toFeedKeyCategories = (keyCategory: string | string[] | undefined): string[] => {
  if (Array.isArray(keyCategory)) return keyCategory;
  return keyCategory ? [keyCategory] : [];
};

export const MonthlyRecapsWidget = ({ metadata }: { metadata: MonthlyRecapsWidgetFilters }) => {
  const [openArticle, setOpenArticle] = useState<MonthlyRecapsArticle | null>(null);
  const articleTriggerRef = useRef<HTMLButtonElement>(null);
  const feedKeyCategories = toFeedKeyCategories(metadata.KeyCategory);
  const { data, isLoading, isSuccess, isError } = useFeedArticles(feedKeyCategories);

  const articles = (data ?? [])
    .filter(item => !metadata.KeyValue || item.symbol.KeyValue === metadata.KeyValue)
    .toSorted(
      (a, b) => new Date(b.transactionTime).getTime() - new Date(a.transactionTime).getTime()
    )
    .map(mapFeedInfoToArticle);

  const hasNoData = isSuccess && articles.length === 0;
  const openArticleDialog = (article: MonthlyRecapsArticle, trigger: HTMLButtonElement) => {
    articleTriggerRef.current = trigger;
    setOpenArticle(article);
  };

  return (
    <div className='flex max-h-[650px] min-h-[200px] flex-col overflow-y-auto'>
      {isLoading && <FeedSkeleton />}
      {hasNoData && <MonthlyRecapsEmptyState />}
      {isError && <MonthlyRecapsErrorState />}

      {!isLoading &&
        !hasNoData &&
        !isError &&
        (metadata.mode === 'single' ? (
          articles[0] && (
            <MonthlyRecapsSingleView
              article={articles[0]}
              onSelectArticle={openArticleDialog}
            />
          )
        ) : (
          <MonthlyRecapsListView
            articles={articles}
            onSelectArticle={openArticleDialog}
          />
        ))}

      <Dialog
        open={openArticle !== null}
        onOpenChange={nextOpen => {
          if (!nextOpen) {
            setOpenArticle(null);
          }
        }}
      >
        {openArticle ? (
          <DialogContent
            className='inset-0 flex h-svh max-h-none w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 p-0'
            finalFocus={articleTriggerRef}
            showCloseButton={false}
          >
            <DialogTitle className='sr-only'>{openArticle.headline}</DialogTitle>
            <DialogDescription className='sr-only'>
              Full monthly recap article and rating controls.
            </DialogDescription>
            <MonthlyRecapArticleDialog
              article={openArticle}
              onBack={() => {
                setOpenArticle(null);
              }}
              onClose={() => {
                setOpenArticle(null);
              }}
            />
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  );
};
